import { useQuery } from '@apollo/client';
import { useAuthState } from '@auth';
import type { Community } from '@communities/community/domain';
import { GET_COMMUNITY } from '@communities/community/graphql';
import { transformErrors } from '@core/adapters/errors';
import { UserRoles, type DomainError } from '@core/domain';
import { useMemo } from 'react';

import { useCurrentCommunityId } from './useCurrentCommunityId';

interface CommunityResponse {
  community: Community;
}

export function useCommunityDetail() {
  const communityId = useCurrentCommunityId();
  const { hasRoleAccess } = useAuthState();

  const { data, loading, error, refetch } = useQuery<CommunityResponse>(GET_COMMUNITY, {
    variables: { id: communityId },
    skip: !communityId,
    fetchPolicy: 'cache-first',
    errorPolicy: 'all', // Return partial data even if some fields fail (e.g., moderationStats on older BFF)
  });

  const community = data?.community || null;

  // Enhanced role checking with hierarchical support
  const isCommunityAdmin = useMemo(() => {
    if (!community) return false;

    return hasRoleAccess(UserRoles.ADMIN, community.communityGroupId, community.id);
  }, [community, hasRoleAccess]);

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  return {
    community,
    isCommunityAdmin,
    loading,
    errors,
    refetch,
  };
}

export default useCommunityDetail;
